/**
 * NYSE market calendar and US Eastern time helpers.
 *
 * Calendar dates are ISO `YYYY-MM-DD` strings (no time zone); instants are
 * `Date`s. Every Eastern conversion goes through `Intl`, so results don't
 * depend on the host's TZ and DST is handled by the ICU tz database.
 *
 * Holidays are rule-generated (NYSE full-day closures; early closes are
 * not modeled), plus a short list of one-off closures.
 */

const EASTERN_TZ = 'America/New_York';

/** Regular session, in Eastern minutes after midnight. */
export const MARKET_OPEN_MINUTES = 9 * 60 + 30;
export const MARKET_CLOSE_MINUTES = 16 * 60;

/** Unscheduled full-day closures (national days of mourning). */
const SPECIAL_CLOSURES = new Set<string>([
  '2012-10-29', // Hurricane Sandy
  '2012-10-30',
  '2018-12-05', // George H.W. Bush
  '2025-01-09', // Jimmy Carter
]);

const easternParts = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function partsOf(instant: Date): { date: string; minutes: number } {
  const parts: Record<string, string> = {};
  for (const p of easternParts.formatToParts(instant)) parts[p.type] = p.value;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** UTC-midnight Date for a calendar date, for weekday/arithmetic only. */
function utcDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

/** Add calendar days to a `YYYY-MM-DD` date. */
export function addCalendarDays(date: string, days: number): string {
  const d = utcDate(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function weekday(date: string): number {
  return utcDate(date).getUTCDay();
}

/** The Eastern calendar date of an instant. */
export function toEasternDate(instant: Date): string {
  return partsOf(instant).date;
}

/** Minutes after Eastern midnight of an instant. */
export function easternMinutesOfDay(instant: Date): number {
  return partsOf(instant).minutes;
}

/** The instant of `hh:mm` Eastern on `date`. */
export function easternInstant(date: string, hh: number, mm = 0): Date {
  // Start from the EST guess, then correct by however far off Eastern it lands.
  const wanted = hh * 60 + mm;
  let guess = new Date(`${date}T${pad(hh)}:${pad(mm)}:00Z`);
  guess = new Date(guess.getTime() + 5 * 3_600_000);
  for (let i = 0; i < 2; i++) {
    const { date: gotDate, minutes } = partsOf(guess);
    const dayDiff = (utcDate(date).getTime() - utcDate(gotDate).getTime()) / 60_000;
    const diff = dayDiff + wanted - minutes;
    if (diff === 0) break;
    guess = new Date(guess.getTime() + diff * 60_000);
  }
  return guess;
}

/** The next Eastern midnight strictly after `instant`. */
export function nextEasternMidnight(instant: Date): Date {
  return easternInstant(addCalendarDays(toEasternDate(instant), 1), 0, 0);
}

/** nth (1-based) `dow` of a month; n = -1 for the last. */
function nthWeekday(year: number, month: number, dow: number, n: number): string {
  if (n > 0) {
    const first = weekday(ymd(year, month, 1));
    return ymd(year, month, 1 + ((dow - first + 7) % 7) + (n - 1) * 7);
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const last = weekday(ymd(year, month, lastDay));
  return ymd(year, month, lastDay - ((last - dow + 7) % 7));
}

/** Western (Gregorian) Easter Sunday — anonymous Gregorian algorithm. */
function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(year, month, day);
}

/** Saturday → Friday, Sunday → Monday. */
function observed(date: string): string {
  const dow = weekday(date);
  if (dow === 6) return addCalendarDays(date, -1);
  if (dow === 0) return addCalendarDays(date, 1);
  return date;
}

const holidayCache = new Map<number, Set<string>>();

function holidaysFor(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const days = new Set<string>();
  // New Year's Day: a Saturday New Year's is not observed on the prior Friday
  // (NYSE rule 7.2), so only the Sunday → Monday shift applies.
  const newYear = ymd(year, 1, 1);
  if (weekday(newYear) !== 6) days.add(observed(newYear));
  days.add(nthWeekday(year, 1, 1, 3)); // MLK Day
  days.add(nthWeekday(year, 2, 1, 3)); // Presidents' Day
  days.add(addCalendarDays(easterSunday(year), -2)); // Good Friday
  days.add(nthWeekday(year, 5, 1, -1)); // Memorial Day
  if (year >= 2022) days.add(observed(ymd(year, 6, 19))); // Juneteenth
  days.add(observed(ymd(year, 7, 4))); // Independence Day
  days.add(nthWeekday(year, 9, 1, 1)); // Labor Day
  days.add(nthWeekday(year, 11, 4, 4)); // Thanksgiving
  days.add(observed(ymd(year, 12, 25))); // Christmas
  holidayCache.set(year, days);
  return days;
}

/** Whether the NYSE is closed all day on `date` for a holiday (weekends excluded). */
export function isNyseHoliday(date: string): boolean {
  return holidaysFor(Number(date.slice(0, 4))).has(date) || SPECIAL_CLOSURES.has(date);
}

/** Whether `date` is an NYSE trading day. */
export function isTradingDay(date: string): boolean {
  const dow = weekday(date);
  return dow !== 0 && dow !== 6 && !isNyseHoliday(date);
}

/** Whether the regular session is open at `instant`. */
export function isMarketOpen(instant: Date): boolean {
  const { date, minutes } = partsOf(instant);
  return isTradingDay(date) && minutes >= MARKET_OPEN_MINUTES && minutes < MARKET_CLOSE_MINUTES;
}

/** The `n`th trading day before `date` (n ≥ 1). */
export function previousTradingDay(date: string, n = 1): string {
  let d = date;
  for (let found = 0; found < n; ) {
    d = addCalendarDays(d, -1);
    if (isTradingDay(d)) found++;
  }
  return d;
}

/** The `n`th trading day after `date` (n ≥ 1). */
export function nextTradingDay(date: string, n = 1): string {
  let d = date;
  for (let found = 0; found < n; ) {
    d = addCalendarDays(d, 1);
    if (isTradingDay(d)) found++;
  }
  return d;
}

/** The most recent trading day whose regular session has closed as of `instant`. */
export function lastCompletedTradingDay(instant: Date): string {
  const { date, minutes } = partsOf(instant);
  if (isTradingDay(date) && minutes >= MARKET_CLOSE_MINUTES) return date;
  return previousTradingDay(date);
}

/** Trading days in [start, end], inclusive. */
export function countTradingDays(start: string, end: string): number {
  let count = 0;
  for (let d = start; d <= end; d = addCalendarDays(d, 1)) {
    if (isTradingDay(d)) count++;
  }
  return count;
}
